import { ReactElement } from "react";

import { Table as BulmaTable } from "react-bulma-components";

import { CheckLabel } from "components/CheckLabel/CheckLabel";
import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { getTransportationTypeName } from "enums/transportationType";
import { getDistanceLocalString, getDistanceKmString } from "helpers/distance";
import { MovementRoute } from "models/movementRoute";
import { PreparedRouteDetail } from "models/preparedRoute";
import Transit from "models/transit";
import * as Table from "molecules/Table";

const renderTransits = (transits: Transit[]) => (
  <BulmaTable>
    <thead>
      <tr>
        <Table.Th>交通手段</Table.Th>
        <Table.Th>路線名</Table.Th>
        <Table.Th>出発地</Table.Th>
        <Table.Th>到着地</Table.Th>
        <Table.Th>移動距離</Table.Th>
        <Table.Th>金額</Table.Th>
        <Table.Th>定期</Table.Th>
      </tr>
    </thead>
    <tbody>
      {transits.map((transit) => (
        <tr key={transit.id}>
          <Table.Td>{transit.type}</Table.Td>
          <Table.Td>{transit.railway}</Table.Td>
          <Table.Td>{transit.departure}</Table.Td>
          <Table.Td>{transit.arrival}</Table.Td>
          <Table.Td>{getDistanceLocalString(transit.distance)}</Table.Td>
          <Table.Td>
            <PriceLabel amount={transit.amount} />
          </Table.Td>
          <Table.Td>{transit.commuterPass}</Table.Td>
        </tr>
      ))}
    </tbody>
  </BulmaTable>
);

const renderMovementRoutes = (movementRoutes: MovementRoute[]) => (
  <Table.Table>
    <thead>
      <tr>
        <Table.ThSmall>交通手段</Table.ThSmall>
        <Table.Th>路線名</Table.Th>
        <Table.Th>出発地</Table.Th>
        <Table.Th>到着地</Table.Th>
        <Table.ThSmall>移動距離</Table.ThSmall>
        <Table.ThSmall>金額</Table.ThSmall>
        <Table.ThUseCommuterPass>定期</Table.ThUseCommuterPass>
      </tr>
    </thead>
    <tbody>
      {movementRoutes.map((movementRoute) => (
        <tr key={movementRoute.id}>
          <Table.Td>
            {getTransportationTypeName(movementRoute.transportationType)}
          </Table.Td>
          <Table.Td>{movementRoute.lineName}</Table.Td>
          <Table.Td>{movementRoute.departureName}</Table.Td>
          <Table.Td>{movementRoute.arrivalName}</Table.Td>
          <Table.Td>{getDistanceKmString(movementRoute.distanceKm)}</Table.Td>
          <Table.Td>
            <PriceLabel amount={movementRoute.fare} />
          </Table.Td>
          <Table.TdUseCommuterPass>
            <CheckLabel checked={movementRoute.useCommuterPass} />
          </Table.TdUseCommuterPass>
        </tr>
      ))}
    </tbody>
  </Table.Table>
);

const renderPreparedRouteDetails = (
  preparedRouteDetails: PreparedRouteDetail[]
) => (
  <Table.Table>
    <thead>
      <tr>
        <Table.ThSmall>交通手段</Table.ThSmall>
        <Table.Th>路線名</Table.Th>
        <Table.Th>出発地</Table.Th>
        <Table.Th>到着地</Table.Th>
        <Table.ThSmall>移動距離</Table.ThSmall>
        <Table.ThSmall>金額</Table.ThSmall>
        <Table.ThUseCommuterPass>定期</Table.ThUseCommuterPass>
      </tr>
    </thead>
    <tbody>
      {preparedRouteDetails.map((preparedRouteDetail) => (
        <tr key={preparedRouteDetail.id}>
          <Table.Td>{preparedRouteDetail.transportationTypeName}</Table.Td>
          <Table.Td>{preparedRouteDetail.lineName}</Table.Td>
          <Table.Td>{preparedRouteDetail.departureName}</Table.Td>
          <Table.Td>{preparedRouteDetail.arrivalName}</Table.Td>
          <Table.Td>
            {getDistanceKmString(preparedRouteDetail.distanceKm)}
          </Table.Td>
          <Table.Td>
            <PriceLabel amount={preparedRouteDetail.fare} />
          </Table.Td>
          <Table.TdUseCommuterPass>
            <CheckLabel checked={preparedRouteDetail.useCommuterPass} />
          </Table.TdUseCommuterPass>
        </tr>
      ))}
    </tbody>
  </Table.Table>
);

export interface TransitsTableProps {
  transits?: Transit[];
  movementRoutes?: MovementRoute[];
  preparedRouteDetails?: PreparedRouteDetail[];
}

export function TransitsTable({
  transits,
  movementRoutes,
  preparedRouteDetails,
}: TransitsTableProps): ReactElement | null {
  if (transits) {
    return <>{renderTransits(transits)}</>;
  }

  if (movementRoutes) {
    return <>{renderMovementRoutes(movementRoutes)}</>;
  }

  if (preparedRouteDetails) {
    return <>{renderPreparedRouteDetails(preparedRouteDetails)}</>;
  }

  return null;
}
